<template>
  <div>
    <vue-editor
      v-if="fontsLoaded"
      :id="'EditorIntroEditor-'+uniqid"
      :ref="uniqref"
      v-model="content"
      :placeholder="$t('outreach.intro.placeholder')"
      :editor-options="editorSettings"
      @selection-change="selectionChange"
      @editor-change="onEditorChange"
      @blur="isFocused = false"
      @focus="isFocused = true"
      @ready="onEditorReady"
    />
    <div class="toolbar__container">
      <div
        v-if="fontsLoaded"
        id="EditorIntroCustomToolbar"
      >
        <!-- default at 12pt -->
        <span class="ql-formats line-right">
          <button
            v-if="useFeature('upload_file')"
            type="button"
            class="ql-attach"
            @click="triggerAddAttachment"
          >
            <editor-attach-file
              v-show="false"
              ref="attachFile"
              :show="addAttachment"
              :clear="clearAttachment"
              :value="attachmentName"
              @input="$emit('upload-attachment', $event)"
              @upload-started="onUploadStarted"
              @progress="attachmentProgress = $event"
              @uploaded="onUploadSuccess"
              @cleaned="onUploadCleaned"
              @error="onUploadFailure"
            />
          </button>
          <button
            type="button"
            class="ql-link"
          />
        </span>
        <span class="ql-formats line-right" style="margin-left: -5px;">
          <button
            type="button"
            class="ql-bold"
          />
          <button
            type="button"
            class="ql-italic"
          />
          <button
            type="button"
            class="ql-underline"
          />
        </span>
        <span
          class="ql-formats line-right bg-no-repeat"
          :class="align_icon"
        >
          <select class="ql-align" @change="onChange($event)">
            <option value />
            <option value="center" />
            <option value="right" />
          </select>
          <base-icon icon="chevron-down-black" style="margin: 4px 0px 0px -6%;" />
        </span>
        <span
          :id="`formatsToolbar${uniqid}`"
          class="ql-formats line-right"
          style="margin-left: -10px;"
        >
          <select class="ql-size">
            <option selected="selected" />
            <option value="small" />
            <option value="large" />
            <option value="huge" />
          </select>
          <base-icon icon="chevron-down-black" style="margin: 4px -10px 0px -25%;" />
        </span>
        <span
          class="ql-formats line-right"
          style="margin-left: -10px;"
        >
          <select
            :ref="`qlFontSelect${uniqid}`"
            class="ql-font"
          >
            <!-- Only using selected="selected" the fonts change correctly as you move the cursor -->
            <!-- Warning: Note that the first paragraph has
            NO SELECTED FONT by default, so the fonts select stays on the last shown -->
            <option
              v-for="(font, index) in textboxFonts"
              :key="`ql-font-${index}`"
              :value="font.font_family"
            />
          </select>
          <base-icon icon="chevron-down-black" style="margin: 4px 0px 0px 3px;" />
        </span>
        <span class="ql-formats">
          <button class="ql-addTag quill-hashtag" />
        </span>
        <attachments
          :files="attachments"
          @clear="triggerClearAttachment"
        />
      </div>
    </div>
  </div>
</template>
<script>

import EditorAttachFile from '@/pages/entries/EditorAttachFile';
import Attachments from '@/components/Widgets/DraftEditor/Attachments';
import VueEditorMixins from '../../_utilities/VueEditorMixins';

// eslint-disable-next-line
const [IDLE, UPLOADING, PATCHING, DONE] = [...Array(4).keys()];

export default {
  components: {
    EditorAttachFile,
    Attachments,
  },
  mixins: [VueEditorMixins],
  props: {
    attachmentName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      align_icon: 'align_left',
      onChange(e) {
        if (e.target.value === 'center') {
          this.align_icon = 'align_center';
        } else if (e.target.value === 'right') {
          this.align_icon = 'align_right';
        } else {
          this.align_icon = 'align_left';
        }
        return this.align_icon;
      },
      selected: '',
      start: false,
      content: '',
      inner_content: {},
      quill: {},
      selection: {},
      editorSettings: {
        formats: [
          'background',
          'bold',
          'color',
          'font',
          'code',
          'italic',
          'link',
          'size',
          'strike',
          // 'script',
          'underline',
          'blockquote',
          'header',
          'indent',
          'list',
          'align',
          'direction',
          'code-block',
          'font',
          'hashtag',
          // 'image'
          // 'video'
        ],
        modules: {
          toolbar: {
            container: '#EditorIntroCustomToolbar',
            handlers: {
              addTag: () => {
                this.addCustomTag('Name');
              },
            },
          },
        },
      },
      addAttachment: false,
      clearAttachment: false,

      uploadStatus: IDLE,
      attachmentTempName: '',
      attachmentProgress: null,
    };
  },
  computed: {
    attachments() {
      const name = this.uploadStatus === UPLOADING ? this.attachmentTempName
        : this.attachmentName || this.attachmentTempName;
      const progress = this.attachmentProgress;
      return !name || !name.length ? []
        : [{
          name,
          progress,
        }];
    },
    isSavingEdit() {
      return this.$store.state.entries.status === 'saving';
    },
  },
  watch: {
    isSavingEdit(newval) {
      if (!newval && this.uploadStatus === PATCHING) {
        // eslint-disable-next-line
        this.uploadStatus === IDLE;
      }
    },
  },
  mounted() {
    this.$store.dispatch('fonts/load');
  },
  methods: {
    triggerAddAttachment() {
      this.addAttachment = true;
      window.setTimeout(() => {
        this.addAttachment = false;
      }, 200);
    },
    triggerClearAttachment() {
      this.clearAttachment = true;
      window.setTimeout(() => {
        this.clearAttachment = false;
      }, 200);
    },
    onUploadStarted(filename) {
      this.attachmentTempName = filename;
      this.attachmentProgress = 0;
      this.uploadStatus = UPLOADING;
    },
    onUploadSuccess(url) {
      this.$emit('upload-attachment', url);
      this.attachmentProgress = null;
      this.uploadStatus = PATCHING;
      this.waitingPatch = true;
    },
    onUploadFailure(error) {
      this.$emit('attachment-error', error);
      this.attachmentTempName = '';
      this.attachmentProgress = null;
      this.uploadStatus = IDLE;
      this.waitingPatch = false;
    },
    onUploadCleaned() {
      this.attachmentTempName = '';
      this.attachmentProgress = null;
      this.uploadStatus = IDLE;
      this.waitingPatch = false;
      this.$emit('clear-attachment');
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep button[class^="ql-"][type="button"]:not(.ql-addTag)  {
  margin-right: 4px !important;
  padding-bottom: 24px !important;
  &.ql-active {
    background-color: rgba(black, 0.1) !important;
    border-radius: 4px !important;
  }
}
  ::v-deep .quill-hashtag {
    border-radius: 4px !important;
    max-height: 24px;
    font-family: 'SilkaLight';
    font-style: normal;
    display: inline-block;
  }

  .toolbar__container {
    position: relative;
    padding-top: 2rem;
    margin: 0 -0.625rem 0 -0.625rem;

    &::v-deep .ql-addTag.quill-hashtag {
      font-family: 'SilkaLight';
      padding: 0 8.5px 0px 8.5px;
      line-height: 24px;
      max-height: 24px;
      margin: 0 !important;
      min-width: 58px !important;
    }
    .ql-font {
      &::v-deep .ql-picker-label,
      ::v-deep .ql-picker-item {
        font-family: $font-family-sans-serif;
        font-weight: 300;
        line-height: 135%;
        font-size: 14px;
        color: #1B1B1B;
      }
    }

  #EditorIntroCustomToolbar {
    position: relative;
    margin: 0;
    margin-top: -37px;
    transform: translateY(1rem);
  }

  .button {
    cursor: pointer;
    opacity: 1;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.3;
    }
  }
}

::v-deep .ql-picker-label {
  border-color: transparent !important;
  max-width: 100px;
}
.line-right{
  border-right: 0.5px solid #D4D4D4;
  padding-right: 8px;
}
// ::v-deep .ql-picker-options {
//   position: absolute !important;
//   top: 0 !important;
//   transform: translateY(-100%);
// }
.ql-toolbar.ql-snow {
  padding: 17px 0px 17px 10px;
}

.align_left{
  background-image: url("~@/assets/icons/align-left.svg") !important;
}
.align_right{
  background-image: url("~@/assets/icons/align-right.svg") !important;
}
.align_center{
  background-image: url("~@/assets/icons/align-center.svg") !important;
}
.bg-no-repeat{
  background-repeat: no-repeat;
  background-position-y: center;
}
.ql-snow .ql-picker-options .ql-picker-item{
  display: none !important;
}
.ql-snow .ql-picker-options .ql-picker-item{
  display: none !important;
}

::v-deep .ql-snow .ql-picker-options .ql-picker-item{
  padding: 8px 12px !important;
}
::v-deep .ql-snow .ql-picker-options {
  padding: unset !important;
}

::v-deep .ql-align .ql-picker-options {
  margin-left: -11px !important;
  margin-bottom: 8px !important;
  border-radius: 4px;
  border: unset !important;
}

::v-deep .ql-align .ql-picker-options .ql-picker-item {
  margin: 9px !important;
  padding: 2px 4px !important;
}

::v-deep .ql-align .ql-picker-options .ql-picker-item svg {
  display: none !important;
}

::v-deep .ql-align .ql-picker-options .ql-picker-item[data-value=""]::before{
  content: url("~@/assets/icons/align-left.svg");
}

::v-deep .ql-align .ql-picker-options .ql-picker-item[data-value=center]::before{
  content: url("~@/assets/icons/align-center.svg");
}

::v-deep .ql-align .ql-picker-options .ql-picker-item[data-value=right]::before{
  content: url("~@/assets/icons/align-right.svg");
}
::v-deep .ql-toolbar.ql-snow .ql-formats {
  margin-right: 11px;
}
</style>
